import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { TimeUnitSelect } from "../../components/AnalysisCollectionsComponents/CollectionChartArea/LineChart/TimeUnitSelect";
import { OptionalRender } from "../../components/Common/OptionalRender";
import { PageTitle } from "../../components/Common/PageTitle";
import { TextFileDownload } from "../../components/Common/TextFileDownload";
import { TimespanPicker } from "../../components/DataDownloadComponents/TimespanPicker";
import { useTimespanPicker } from "../../components/DataDownloadComponents/TimespanPicker/hooks";
import Checkbox from "../../components/FormElement/Checkbox";
import { AccountType } from "../../models/AccountData";
import { LocationData } from "../../models/LocationData";
import { OrganizationData } from "../../models/OrganizationData";
import DataSvc from "../../services/dataSvc";
import { buildCsvFilename } from "../../utils/csvUtils";

import "./styles.scss";

type IAdminOrganizationDataDownloadPageProps = RouteComponentProps<any>;

const ORG_NAME_HEADERS = ["団体名", "Organization Name"];
const FACILITY_NAME_HEADERS = [
  "施設名",
  "Facility Name",
  "Location Name",
  "店舗名",
];
const ORG_ID_HEADERS = ["Org ID", "Organization ID"];
const FAC_ID_HEADERS = ["Fac ID", "Facility ID"];

const findHeaderIndex = (headers: string[], candidates: string[]) => {
  return headers.findIndex((header) =>
    candidates.some(
      (candidate) => candidate.toLowerCase() === header.trim().toLowerCase()
    )
  );
};

const parseCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
};

const stringifyCsvLine = (cells: string[]) => {
  return cells
    .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
    .join(",");
};

const normalizeCsvWithOrgAndFacilityIds = ({
  csv,
  organization,
  locations,
}: {
  csv: string;
  organization: OrganizationData;
  locations: LocationData[];
}) => {
  const lines = csv
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => line.trim());

  if (lines.length === 0) {
    return "";
  }

  const rows = lines.map((line) => parseCsvLine(line));
  const headers = rows[0];

  const locationIdByName = new Map<string, string | number>();
  locations.forEach((location) => {
    locationIdByName.set(location.name, location.id);
  });

  let orgNameIndex = findHeaderIndex(headers, ORG_NAME_HEADERS);
  if (orgNameIndex === -1) {
    headers.unshift("団体名");
    rows.slice(1).forEach((row) => {
      row.unshift(organization.name);
    });
    orgNameIndex = 0;
  }

  let orgIdIndex = findHeaderIndex(headers, ORG_ID_HEADERS);
  if (orgIdIndex === -1) {
    orgIdIndex = orgNameIndex + 1;
    headers.splice(orgIdIndex, 0, "Org ID");
    rows.slice(1).forEach((row) => {
      row.splice(orgIdIndex, 0, String(organization.id));
    });
  }

  let facilityNameIndex = findHeaderIndex(headers, FACILITY_NAME_HEADERS);
  let facIdIndex = findHeaderIndex(headers, FAC_ID_HEADERS);
  if (facilityNameIndex !== -1 && facIdIndex === -1) {
    facIdIndex = facilityNameIndex + 1;
    headers.splice(facIdIndex, 0, "Fac ID");
    rows.slice(1).forEach((row) => {
      row.splice(facIdIndex, 0, "");
    });
    facilityNameIndex = findHeaderIndex(headers, FACILITY_NAME_HEADERS);
  }

  rows.slice(1).forEach((row) => {
    if (orgNameIndex !== -1 && !row[orgNameIndex]) {
      row[orgNameIndex] = organization.name;
    }

    if (orgIdIndex !== -1 && !row[orgIdIndex]) {
      row[orgIdIndex] = String(organization.id);
    }

    if (facilityNameIndex !== -1 && facIdIndex !== -1) {
      const facilityName = row[facilityNameIndex] || "";
      const facilityId = locationIdByName.get(facilityName);
      if (facilityId !== undefined && !row[facIdIndex]) {
        row[facIdIndex] = String(facilityId);
      }
    }
  });

  return rows.map((row) => stringifyCsvLine(row)).join("\n");
};

const mergeCsvContents = (csvContents: string[]) => {
  const mergedLines: string[] = [];

  csvContents.forEach((csv, index) => {
    const lines = csv
      .replace(/\r\n/g, "\n")
      .split("\n")
      .filter((line) => line.trim());

    if (lines.length === 0) {
      return;
    }

    if (index === 0) {
      mergedLines.push(...lines);
      return;
    }

    mergedLines.push(...lines.slice(1));
  });

  return mergedLines.join("\n");
};

const AdminOrganizationDataDownloadPage: React.FunctionComponent<
  IAdminOrganizationDataDownloadPageProps
> = () => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`adminOrganizationDataDownloadPage.${key}`),
    [_t]
  );

  const metricsTimespan = useTimespanPicker();
  const activityLogsTimespan = useTimespanPicker();
  const locationStatsTimespan = useTimespanPicker();

  const [timeUnit, setTimeUnit] = useState<"day" | "week" | "month">("day");
  const [replyPercentage, setReplyPercentage] = useState(true);
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<
    string[]
  >([]);
  const [initializedSelection, setInitializedSelection] = useState(false);

  const organizationsQuery = useQuery({
    queryKey: ["admin-organization-data-download-organizations"],
    queryFn: () =>
      DataSvc.getOrganizations({
        limit: 10000,
        sort: "name",
      }),
  });

  const allOrganizations = useMemo(
    () => organizationsQuery.data?.list ?? [],
    [organizationsQuery.data?.list]
  );

  useEffect(() => {
    if (!initializedSelection && allOrganizations.length > 0) {
      setSelectedOrganizationIds(allOrganizations.map((org) => String(org.id)));
      setInitializedSelection(true);
    }
  }, [initializedSelection, allOrganizations]);

  const selectedOrganizations = useMemo(() => {
    const selectedSet = new Set(selectedOrganizationIds);
    return allOrganizations.filter((org) => selectedSet.has(String(org.id)));
  }, [allOrganizations, selectedOrganizationIds]);

  const toggleOrganization = (organization: OrganizationData) => {
    const orgId = String(organization.id);
    setSelectedOrganizationIds((prev) =>
      prev.includes(orgId)
        ? prev.filter((id) => id !== orgId)
        : [...prev, orgId]
    );
  };

  const selectAll = () => {
    setSelectedOrganizationIds(allOrganizations.map((org) => String(org.id)));
  };

  const clearSelection = () => {
    setSelectedOrganizationIds([]);
  };

  const getSelectedOrganizationsWithLocations = useCallback(async () => {
    const organizationsWithLocations = await Promise.all(
      selectedOrganizations.map(async (organization) => {
        const locationResponse = await DataSvc.getLocations({
          organization_id: [organization.id],
          summary_mode: true,
          limit: 10000,
        });

        return {
          organization,
          locations: locationResponse?.list ?? [],
        };
      })
    );

    return organizationsWithLocations.filter(
      ({ locations }) => locations.length > 0
    );
  }, [selectedOrganizations]);

  const filenameOrganizationName = useMemo(
    () =>
      selectedOrganizations.length === 1
        ? selectedOrganizations[0].name
        : `${selectedOrganizations.length}_organizations`,
    [selectedOrganizations]
  );

  const metricsQueryKey = useMemo(
    () => [
      "admin-download-metrics-multi-org",
      metricsTimespan.timespan,
      timeUnit,
      [...selectedOrganizationIds].sort(),
    ],
    [metricsTimespan.timespan, timeUnit, selectedOrganizationIds]
  );

  const metricsQueryFunction = useCallback(async () => {
    if (selectedOrganizations.length === 0) {
      throw new Error(t("organizationSelect.select_at_least_one"));
    }

    const organizationsWithLocations =
      await getSelectedOrganizationsWithLocations();

    const csvContents = await Promise.all(
      organizationsWithLocations.map(async ({ organization, locations }) => {
        const rawCsv = await DataSvc.getFacilityDataMetrics({
          organizationId: String(organization.id),
          timespan: metricsTimespan.timespan,
          timeUnit,
          locations,
        });

        return normalizeCsvWithOrgAndFacilityIds({
          csv: rawCsv,
          organization,
          locations,
        });
      })
    );

    const nonEmptyCsvContents = csvContents.filter(
      (csv) => csv.trim().length > 0
    );
    if (nonEmptyCsvContents.length === 0) {
      throw new Error(t("downloadMetrics.no_data"));
    }

    const content = mergeCsvContents(nonEmptyCsvContents);
    const filename = buildCsvFilename(
      {
        startDate: metricsTimespan.timespan.startDate?.format("YYYY-MM-DD"),
        endDate: metricsTimespan.timespan.endDate?.format("YYYY-MM-DD"),
        organizationName: filenameOrganizationName,
        contentDescription: "metrics",
      },
      _t
    );

    return {
      filename,
      content,
    };
  }, [
    selectedOrganizations,
    getSelectedOrganizationsWithLocations,
    metricsTimespan.timespan,
    timeUnit,
    filenameOrganizationName,
    _t,
    t,
  ]);

  const activityLogsQueryKey = useMemo(
    () => [
      "admin-download-activity-logs-multi-org",
      activityLogsTimespan.timespan,
      [...selectedOrganizationIds].sort(),
    ],
    [activityLogsTimespan.timespan, selectedOrganizationIds]
  );

  const activityLogsQueryFunction = useCallback(async () => {
    if (selectedOrganizations.length === 0) {
      throw new Error(t("organizationSelect.select_at_least_one"));
    }

    const organizationsWithLocations =
      await getSelectedOrganizationsWithLocations();

    const csvContents = await Promise.all(
      organizationsWithLocations.map(async ({ organization, locations }) => {
        const rawCsv = await DataSvc.getFacilityDataActivityLogs({
          organizationId: String(organization.id),
          timespan: activityLogsTimespan.timespan,
          locations,
        });

        return normalizeCsvWithOrgAndFacilityIds({
          csv: rawCsv,
          organization,
          locations,
        });
      })
    );

    const nonEmptyCsvContents = csvContents.filter(
      (csv) => csv.trim().length > 0
    );
    if (nonEmptyCsvContents.length === 0) {
      throw new Error(t("downloadActivityLogs.no_data"));
    }

    const content = mergeCsvContents(nonEmptyCsvContents);
    const filename = buildCsvFilename(
      {
        startDate:
          activityLogsTimespan.timespan.startDate?.format("YYYY-MM-DD"),
        endDate: activityLogsTimespan.timespan.endDate?.format("YYYY-MM-DD"),
        organizationName: filenameOrganizationName,
        contentDescription: "activity_logs",
      },
      _t
    );

    return {
      filename,
      content,
    };
  }, [
    selectedOrganizations,
    getSelectedOrganizationsWithLocations,
    activityLogsTimespan.timespan,
    filenameOrganizationName,
    _t,
    t,
  ]);

  const locationStatsQueryKey = useMemo(
    () => [
      "admin-download-location-stats-multi-org",
      locationStatsTimespan.timespan,
      replyPercentage,
      [...selectedOrganizationIds].sort(),
    ],
    [locationStatsTimespan.timespan, replyPercentage, selectedOrganizationIds]
  );

  const locationStatsQueryFunction = useCallback(async () => {
    if (selectedOrganizations.length === 0) {
      throw new Error(t("organizationSelect.select_at_least_one"));
    }

    const organizationsWithLocations =
      await getSelectedOrganizationsWithLocations();

    const csvContents = await Promise.all(
      organizationsWithLocations.map(async ({ organization, locations }) => {
        const rawCsv = await DataSvc.getFacilityDataLocationStats({
          organizationId: String(organization.id),
          timespan: locationStatsTimespan.timespan,
          locations,
          params: {
            reply_percentage: replyPercentage,
          },
        });

        return normalizeCsvWithOrgAndFacilityIds({
          csv: rawCsv,
          organization,
          locations,
        });
      })
    );

    const nonEmptyCsvContents = csvContents.filter(
      (csv) => csv.trim().length > 0
    );
    if (nonEmptyCsvContents.length === 0) {
      throw new Error(t("downloadLocationStats.no_data"));
    }

    const content = mergeCsvContents(nonEmptyCsvContents);
    const filename = buildCsvFilename(
      {
        startDate:
          locationStatsTimespan.timespan.startDate?.format("YYYY-MM-DD"),
        endDate: locationStatsTimespan.timespan.endDate?.format("YYYY-MM-DD"),
        organizationName: filenameOrganizationName,
        contentDescription: "location_stats",
      },
      _t
    );

    return {
      filename,
      content,
    };
  }, [
    selectedOrganizations,
    getSelectedOrganizationsWithLocations,
    locationStatsTimespan.timespan,
    replyPercentage,
    filenameOrganizationName,
    _t,
    t,
  ]);

  return (
    <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
      <div className="right-content data-download-page admin-organization-data-download-page">
        <div className="top-title-bar flex-grid">
          <PageTitle text={t("data_download")} />
        </div>

        <div className="organization-select">
          <div className="organization-select-list">
            {allOrganizations.map((organization) => {
              const orgId = String(organization.id);
              return (
                <Checkbox
                  key={orgId}
                  checked={selectedOrganizationIds.includes(orgId)}
                  onChange={() => toggleOrganization(organization)}
                  id={orgId}
                  label={organization.name}
                  className="organization-select-checkbox"
                />
              );
            })}
          </div>
          <div className="organization-select-controls">
            <button className="btn btn-blue" onClick={selectAll}>
              {t("organizationSelect.select_all")}
            </button>
            <button className="btn btn-border" onClick={clearSelection}>
              {t("organizationSelect.clear_selection")}
            </button>
          </div>
        </div>

        <div className="white-border-panel download-metrics">
          <h3>{t("downloadMetrics.download_metrics")}</h3>
          <div className="content">
            <div className="download-metrics-controls">
              <TimespanPicker timespan={metricsTimespan} />
              <TimeUnitSelect timeUnit={timeUnit} setTimeUnit={setTimeUnit} />
            </div>
            <TextFileDownload
              queryKey={metricsQueryKey}
              queryFunction={metricsQueryFunction}
            />
          </div>
        </div>

        <div className="white-border-panel download-activity-logs">
          <h3>{t("downloadActivityLogs.download_activity_logs")}</h3>
          <div className="content">
            <div className="download-activity-logs-controls">
              <TimespanPicker timespan={activityLogsTimespan} />
            </div>
            <TextFileDownload
              queryKey={activityLogsQueryKey}
              queryFunction={activityLogsQueryFunction}
            />
          </div>
        </div>

        <div className="white-border-panel download-metrics">
          <h3>{t("downloadLocationStats.download_location_stats")}</h3>
          <div className="content">
            <div className="download-metrics-controls">
              <TimespanPicker timespan={locationStatsTimespan} />
            </div>
            <div>
              <Checkbox
                id="reply_percentage"
                label={t("downloadLocationStats.reply_percentage")}
                checked={replyPercentage}
                onChange={() => setReplyPercentage((prev) => !prev)}
              />
            </div>
            <TextFileDownload
              queryKey={locationStatsQueryKey}
              queryFunction={locationStatsQueryFunction}
            />
          </div>
        </div>
      </div>
    </OptionalRender>
  );
};

export default withRouter(AdminOrganizationDataDownloadPage);
